import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './PasteForm.css';
import AttachmentSection from './AttachmentSection';

const PasteForm = ({ keyId, paste, setPaste, loading, setLoading, setAttachedFile, attachedFile, fetchedFileInfo, setfetchedFileInfo, showFileSizeAlert }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const caracteresMax = 3075;
  const maxSize = 15 * 1024 * 1024; // 15MB

  // funcion que valida el tamaño de los archivos
  const filterValidFiles = (files, maxSize, showFileSizeAlert) => {
    const validFiles = [];
    files.forEach(file => {
      if (file.size > maxSize) {
        showFileSizeAlert(file.name, maxSize / 1024 / 1024);
      } else {
        validFiles.push(file);
      }
    });
    return validFiles;
  };

  // hace click en el input file
  const handleFileButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);

    // valida cada archivo individualmente
    const validFiles = filterValidFiles(files, maxSize, showFileSizeAlert);

    // solo agrega los archivos validos
    if (validFiles.length > 0) {
      setAttachedFile(prevFiles => [...prevFiles, ...validFiles]);
    }
    // sirve para volver a seleccionar el mismo archivo despues de sacarlo
    event.target.value = null;
  };

  const handleRemoveAttachment = (index) => {
    const newAttachments = [...attachedFile];
    newAttachments.splice(index, 1);
    setAttachedFile(newAttachments);
  }

  const handlePaste = (event) => {
    const items = event.clipboardData.items;
    const files = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile();
        files.push(file);
        event.preventDefault();
      }
    }
    // valida cada archivo individualmente
    const validFiles = filterValidFiles(files, maxSize, showFileSizeAlert);

    // solo agrega los archivos validos
    if (validFiles.length > 0) {
      setAttachedFile(prevFiles => [...prevFiles, ...validFiles]);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false); // Quita el estado de drag
    const files = Array.from(event.dataTransfer.files);

    // valida cada archivo individualmente
    const validFiles = filterValidFiles(files, maxSize, showFileSizeAlert);

    // solo agrega los archivos validos
    if (validFiles.length > 0) {
      setAttachedFile(prevFiles => [...prevFiles, ...validFiles]);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragActive(true); // Activa el estado de drag
  };

  const handleDragLeave = (event) => {
    // Solo desactiva si realmente sale del area del textarea
    if (event.target === event.currentTarget) {
      setDragActive(false);
    }
  };

  return (
    <>
      {dragActive && (
        <div className="paste-overlay">
          <span>¡Suelta el archivo para adjuntarlo!</span>
        </div>
      )}
      <div className="mb-3 position-relative">
        <label htmlFor="content" className="form-label">{t('pasteForm.contentLabel')}</label>
        <div className="progress">
          <div className="progress-bar bg-warning" role="progressbar" style={{ width: `${(paste.length / caracteresMax) * 100}%` }} aria-valuenow="40" aria-valuemin="0" aria-valuemax="100"></div>
        </div>
        {paste.length > 2899 && (
          <span className="text-secondary small">
            {paste.length}/{caracteresMax} {t('pasteForm.charactersLabel')}
          </span>
        )}
        <textarea
          id="content"
          className={`form-control bg-dark text-light border-secondary ${dragActive ? 'border-warning bg-secondary bg-opacity-25' : ''}`}
          rows="5"
          placeholder={
            dragActive
              ? t('pasteForm.placeholder.dragActive')
              : (loading ? t('pasteForm.placeholder.loading') : t('pasteForm.placeholder.default'))
          }
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
          disabled={loading}
          onPaste={handlePaste}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          maxLength={3075}
        ></textarea>

        {loading && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center rounded"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
            <div className="spinner-grow text-primary" role="status">
              <span className="visually-hidden">{t('pasteForm.loadingText')}</span>
            </div>
          </div>
        )}
        {/* input invisible que es usado por handleFileButtonClick */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          multiple
        />

        <div className="d-flex justify-content-between align-items-center mt-2">
          <div className="d-flex flex-wrap">
            {attachedFile && attachedFile.length > 0 && (
              <div className="file-badges">
                {attachedFile.map((file, index) => (
                  <span
                    key={index}
                    className="badge bg-secondary me-1 mb-1 d-flex align-items-center file-badges"
                    style={{ maxWidth: "300px" }}
                    title={file.name}
                  >
                    <span className="badge-filename">{file.name}</span>
                    <button
                      className="btn-close btn-close-white ms-1 p-0"
                      style={{ fontSize: "0.8rem" }}
                      onClick={() => handleRemoveAttachment(index)}
                      aria-label="Close"
                    ></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <button className="btn btn-warning btn-sm" onClick={handleFileButtonClick} type="button" disabled={loading}>
            <i className="bi bi-paperclip"></i>
          </button>
        </div>

        {fetchedFileInfo && fetchedFileInfo.length > 0 && (
          <AttachmentSection
            fetchedFileInfo={fetchedFileInfo}
            setfetchedFileInfo={setfetchedFileInfo}
            //estos datos los uso para reconstruir el paste y ahorrar una consulta a la api (servidores free)
            pasteText={paste}
            keyId={keyId}
          />
        )}

      </div>
    </>
  )
};

export default PasteForm;